import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "../../../redux/store";
import { SliderItem, FetchParams, SliderState } from "./types/slider-types";

const API_BASE = process.env.REACT_APP_API_URL || "";

async function handleResp(res: Response) {
  const text = await res.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!res.ok) {
    const msg =
      (payload && (payload.message || payload.error)) ||
      res.statusText ||
      "Request failed";
    throw new Error(msg);
  }
  return payload;
}

/**
 * Thunks (use Bearer token from state.auth.token)
 */
export const fetchSliders = createAsyncThunk<
  SliderItem[],
  FetchParams | undefined
>(
  "sliders/fetch",
  async (params = { page: 1, perPage: 50 }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const res = await fetch(
        `${API_BASE}/sliders?page=${params.page ?? 1}&per_page=${
          params.perPage ?? 50
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const payload: any = await handleResp(res);
      if (Array.isArray(payload)) return payload;
      if (payload?.data && Array.isArray(payload.data)) return payload.data;
      if (payload?.data?.data && Array.isArray(payload.data.data))
        return payload.data.data;
      return payload?.data ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch sliders");
    }
  }
);

export const createSliderThunk = createAsyncThunk<
  SliderItem,
  { title?: string; url?: string; image?: File | null },
  { rejectValue: string; state: RootState }
>("sliders/create", async (payload, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const form = new FormData();
    if (payload.title !== undefined)
      form.append("title", payload.title as string);
    if (payload.url !== undefined) form.append("url", payload.url as string);
    if (payload.image) form.append("image", payload.image);
    const res = await fetch(`${API_BASE}/sliders`, {
      method: "POST",
      body: form,
      headers: { Authorization: `Bearer ${token}` },
    });
    const data: any = await handleResp(res);
    return data?.data ?? data;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to create slider");
  }
});

export const updateSliderThunk = createAsyncThunk<
  SliderItem,
  { id: number; title?: string; url?: string; image?: File | null },
  { rejectValue: string; state: RootState }
>(
  "sliders/update",
  async ({ id, title, url, image }, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = (state as any).auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const form = new FormData();
      if (title !== undefined) form.append("title", title as string);
      if (url !== undefined) form.append("url", url as string);
      if (image) form.append("image", image);
      form.append("_method", "PUT");
      const res = await fetch(`${API_BASE}/sliders/${id}`, {
        method: "POST",
        body: form,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: any = await handleResp(res);
      return data?.data ?? data;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to update slider");
    }
  }
);

export const deleteSliderThunk = createAsyncThunk<
  number,
  number,
  { rejectValue: string; state: RootState }
>("sliders/delete", async (id, { rejectWithValue, getState }) => {
  const state = getState() as RootState;
  const token = (state as any).auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const res = await fetch(`${API_BASE}/sliders/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    await handleResp(res);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to delete slider");
  }
});

/**
 * Slice
 */
const initialState: SliderState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
};

const sliderSlice = createSlice({
  name: "sliders",
  initialState,
  reducers: {
    setSliders(state, action: PayloadAction<SliderItem[]>) {
      state.items = action.payload;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearAll(state) {
      state.items = [];
      state.loading = false;
      state.saving = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSliders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSliders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchSliders.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to fetch sliders";
      })

      .addCase(createSliderThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createSliderThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.items = [action.payload, ...state.items];
      })
      .addCase(createSliderThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to create slider";
      })

      .addCase(updateSliderThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSliderThunk.fulfilled, (state, action) => {
        state.saving = false;
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(updateSliderThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to update slider";
      })

      .addCase(deleteSliderThunk.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteSliderThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.items = state.items.filter((i) => i.id !== action.payload);
      })
      .addCase(deleteSliderThunk.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to delete slider";
      });
  },
});

export const { setSliders, clearError, clearAll } = sliderSlice.actions;

/**
 * Selectors
 */
export const selectSliders = (state: RootState) => state.sliders.items;
export const selectSlidersLoading = (state: RootState) => state.sliders.loading;
export const selectSlidersSaving = (state: RootState) => state.sliders.saving;
export const selectSlidersError = (state: RootState) => state.sliders.error;
export const selectSliderById = (state: RootState, id?: number) =>
  state.sliders.items.find((i) => i.id === id);

export default sliderSlice.reducer;
